import { compareStrings, highlightSearch } from '@/filters/helpers';
import { userHasMatchingRole } from '@/lib/userRoles';
import clone from 'lodash/clone';
import { FEATURES } from '@/access/features';
import { ROLES } from '@/access/roles';

export default {
  users: (state) => {
    return state.users;
  },
  usersLoaded: (state) => {
    return state.usersLoaded;
  },
  activeUsers: (state) => {
    return state.users.filter(({ status }) => {
      return status == '1' || typeof status === 'undefined';
    });
  },
  archivedUsers: (state) => {
    return state.users.filter((user) => user.status == '0');
  },
  filteredUsers: (state, getters, rootState) => {
    return (active = true) => {
      const users = active ? getters.activeUsers : getters.archivedUsers;
      const orgRoles = rootState.roles?.roles || [];
      return filterUsers(users, state.usersSearch, orgRoles);
    };
  },
  usersOptions: (state, getters) => {
    return formatUsers(getters.activeUsers);
  },
  usersView: (state) => {
    return state.usersView;
  },
  usersSearch: (state) => {
    return state.usersSearch;
  },
  usersByIdMap(state) {
    const map = Object.create(null);
    for (const user of state.users) {
      map[user.id] = user;
    }
    return map;
  },
  singleUser(state, getters) {
    return (userId) => getters.usersByIdMap[userId] ?? null;
  },
  userLoading(state) {
    return state.userLoading;
  },
  currentUser: (state) => {
    return state.currentUser;
  },
  userDefaults: (state) => {
    return state.currentUser?.defaults || {};
  },
  userTeam: (state) => {
    return state.currentUser?.selected_team;
  },
  isUserLoggedIn: (state) => {
    return !!state.currentUser;
  },
  currentUserLoaded: (state) => {
    return state.currentUserLoaded;
  },
  isAdmin: (state) => {
    return (state.currentUser || {}).role === ROLES.ADMIN;
  },
  isContactInviteEnabled: (state) => {
    return state.currentUser?.org_plan === 'portal_search';
  },
  // check is feature enabled for organization
  isFeatureEnabled: (state) => {
    return (feature) => {
      const features = state?.currentUser?.features || {};
      return features[feature] || false;
    };
  },
  // check if user has permission
  hasPermission: (state) => {
    return (feature) => {
      if (!state.currentUser) return false;
      const { feature_flags } = state.currentUser;
      return (feature_flags || []).includes(feature);
    };
  },
  // check if user has any of the given permissions (OR condition)
  hasAnyPermission: (state) => {
    return (permissions) => {
      if (!state.currentUser) return false;
      if (!permissions?.length) return true;
      const { feature_flags } = state.currentUser;
      return permissions.some((p) => (feature_flags || []).includes(p));
    };
  },
  isVectorRecruiting: (state, getters) => {
    return getters.isFeatureEnabled(FEATURES.VMC_RECRUITING);
  },
  hasAccessToPage: (state, getters) => {
    return (pageMeta) => {
      const { feature, permission, permissions } = pageMeta;

      // Single permission check
      const hasSinglePermission =
        !permission || getters.hasPermission(permission);

      // Multiple permissions check (OR condition - user needs ANY of the permissions)
      const hasAnyPermission =
        !permissions?.length || getters.hasAnyPermission(permissions);

      const featureEnabled = !feature || getters.isFeatureEnabled(feature);

      return featureEnabled && hasSinglePermission && hasAnyPermission;
    };
  },
  hasAchievement: (state) => {
    return (achievement) => {
      return (state.achievements || []).includes(achievement);
    };
  },
  achievementsLoaded: (state) => {
    return state.achievementsLoaded;
  },
  allPermissions: (state) => {
    return state.allPermissions || [];
  },
  usersTimezones: (state) => {
    return state.usersTimezones;
  },
};

const formatUsers = (arr) => {
  return arr.map((user) => {
    return {
      label: user.name,
      value: user.id,
      role: user.role,
      photo: user.photo,
    };
  });
};

const getName = (user) => {
  return user?.name;
};

const filterUsers = (arr, search, orgRoles = []) => {
  const users = arr
    .map((user) => {
      const fullName = getName(user);
      return Object.assign(clone(user), {
        fullName: highlightSearch(fullName, search),
      });
    })
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  if (!search) return users;

  return users.filter((user) => {
    return (
      compareStrings(user.name, search) ||
      userHasMatchingRole(user, search, orgRoles)
    );
  });
};
